import React from 'react';
import { X, Calendar, MapPin, Clock, Euro, Eye, Users } from 'lucide-react';
import DOMPurify from 'dompurify';
import './JobViewModal.scss';

// Match the frontend's RichTextEditor sanitizer config so what employers type
// in the rich editor renders identically inside the admin viewer. Legacy plain
// text values still get newline → <br> conversion.
const RICH_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'h3', 'h4', 'ul', 'ol', 'li', 'a', 'span', 'div', 'font',
];
const RICH_ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'color', 'size'];
const looksLikeHtml = (s: string): boolean => /<[a-z][\s\S]*>/i.test(s);
const renderRich = (value?: string | null): { __html: string } => {
  if (!value) return { __html: '' };
  const html = looksLikeHtml(value) ? value : value.replace(/\n/g, '<br>');
  return {
    __html: DOMPurify.sanitize(html, {
      ALLOWED_TAGS: RICH_ALLOWED_TAGS,
      ALLOWED_ATTR: RICH_ALLOWED_ATTR,
    }),
  };
};

interface Job {
  id: number;
  title: string;
  job_language?: string;
  description: string;
  short_description?: string;
  location: string;
  work_field?: string;
  position_type?: string;
  job_type: string;
  work_time?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  hide_salary?: boolean;
  status: 'draft' | 'pending' | 'published' | 'closed' | 'archived' | 'declined';
  views_count?: number;
  applications_count?: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  company_name?: string;
  employer_email?: string;
  requirements?: string;
  responsibilities?: string;
  benefits?: string;
  company_offers?: string;
  additional_info?: string;
  guarantee_confidentiality?: boolean;
  inform_selected_only?: boolean;
  start_date_type?: string;
  application_deadline?: string;
  company_activity?: string;
  contact_email?: string;
  company_description?: string;
}

interface JobViewModalProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
}

const JobViewModal: React.FC<JobViewModalProps> = ({ job, isOpen, onClose }) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'draft': 'Juodraštis',
      'pending': 'Laukia patvirtinimo',
      'published': 'Publikuotas',
      'closed': 'Uždarytas',
      'archived': 'Archyvuotas',
      'declined': 'Atmestas',
    };
    return statusMap[status] || status;
  };

  const formatSalary = (salaryMin?: number, salaryMax?: number, currency: string = 'EUR') => {
    if (!salaryMin && !salaryMax) {
      return 'Derinama';
    }
    
    const currencySymbol = currency === 'EUR' ? '€' : currency;
    
    if (salaryMin && salaryMax) {
      return `${salaryMin} - ${salaryMax} ${currencySymbol}`;
    } else if (salaryMin) {
      return `nuo ${salaryMin} ${currencySymbol}`;
    } else if (salaryMax) {
      return `iki ${salaryMax} ${currencySymbol}`;
    }
    
    return 'Derinama';
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'published':
        return 'status-badge status-badge--published';
      case 'draft':
        return 'status-badge status-badge--draft';
      case 'pending':
        return 'status-badge status-badge--pending';
      case 'closed':
        return 'status-badge status-badge--closed';
      case 'archived':
        return 'status-badge status-badge--archived';
      case 'declined':
        return 'status-badge status-badge--declined';
      default:
        return 'status-badge';
    }
  };

  return (
    <div className="job-view-modal-overlay" onClick={onClose}>
      <div className="job-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{job.title}</h2>
            <span className={getStatusClass(job.status)}>
              {formatStatus(job.status)}
            </span>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="job-meta">
            <div className="meta-grid">
              <div className="meta-item">
                <MapPin size={16} />
                <span>{job.location}</span>
              </div>
              {job.work_field && (
                <div className="meta-item">
                  <span>📂 {job.work_field}</span>
                </div>
              )}
              {job.position_type && (
                <div className="meta-item">
                  <span>👤 {job.position_type}</span>
                </div>
              )}
              <div className="meta-item">
                <Clock size={16} />
                <span>{job.job_type || 'Nenurodyta'}</span>
              </div>
              {job.work_time && (
                <div className="meta-item">
                  <span>⏰ {job.work_time}</span>
                </div>
              )}
              <div className="meta-item">
                <Euro size={16} />
                <span>{job.hide_salary ? 'Atlyginimas nenurodomas' : formatSalary(job.salary_min, job.salary_max, job.salary_currency)}</span>
              </div>
              {job.start_date_type && (
                <div className="meta-item">
                  <span>🚀 Pradžia: {job.start_date_type}</span>
                </div>
              )}
              {job.job_language && (
                <div className="meta-item">
                  <span>🌐 {job.job_language}</span>
                </div>
              )}
              <div className="meta-item">
                <Eye size={16} />
                <span>{job.views_count || 0} peržiūrų</span>
              </div>
              <div className="meta-item">
                <Users size={16} />
                <span>{job.applications_count || 0} prašymų</span>
              </div>
              <div className="meta-item">
                <Calendar size={16} />
                <span>Sukurta: {formatDate(job.created_at)}</span>
              </div>
            </div>
          </div>

          <div className="company-info">
            <h3>Įmonės informacija</h3>
            <div className="company-details">
              <p><strong>Įmonė:</strong> {job.company_name || 'Nenurodyta'}</p>
              {job.company_activity && <p><strong>Veiklos sritis:</strong> {job.company_activity}</p>}
              <p><strong>Kontaktinis el. paštas:</strong> {job.contact_email || job.employer_email || 'Nenurodyta'}</p>
              {job.company_description && (
                <div className="company-desc">
                  <strong>Įmonės aprašymas:</strong>
                  <p>{job.company_description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Checkboxes section */}
          {(job.guarantee_confidentiality || job.inform_selected_only) && (
            <div className="job-section badges">
              {job.guarantee_confidentiality && (
                <span className="badge">✓ Konfidencialumą garantuojame</span>
              )}
              {job.inform_selected_only && (
                <span className="badge">✓ Informuosime tik atrinktus kandidatus</span>
              )}
            </div>
          )}

          {job.short_description && (
            <div className="job-section">
              <h3>Trumpas aprašymas</h3>
              <p>{job.short_description}</p>
            </div>
          )}

          <div className="job-section">
            <h3>Darbo aprašymas</h3>
            <div className="job-description" dangerouslySetInnerHTML={renderRich(job.description)} />
          </div>

          {job.requirements && (
            <div className="job-section">
              <h3>Reikalavimai kandidatams</h3>
              <div className="job-content" dangerouslySetInnerHTML={renderRich(job.requirements)} />
            </div>
          )}

          {job.benefits && (
            <div className="job-section">
              <h3>Privalumai</h3>
              <div className="job-content" dangerouslySetInnerHTML={renderRich(job.benefits)} />
            </div>
          )}

          {job.company_offers && (
            <div className="job-section">
              <h3>Įmonė siūlo</h3>
              <div className="job-content" dangerouslySetInnerHTML={renderRich(job.company_offers)} />
            </div>
          )}

          {job.additional_info && (
            <div className="job-section">
              <h3>Papildoma informacija</h3>
              <div className="job-content" dangerouslySetInnerHTML={renderRich(job.additional_info)} />
            </div>
          )}

          {job.responsibilities && (
            <div className="job-section">
              <h3>Pareigos ir atsakomybės</h3>
              <div className="job-content" dangerouslySetInnerHTML={renderRich(job.responsibilities)} />
            </div>
          )}

          {job.application_deadline && (
            <div className="job-section">
              <h3>Prašymus siųsti iki</h3>
              <p>{formatDate(job.application_deadline)}</p>
            </div>
          )}

          <div className="job-dates">
            <div className="date-item">
              <strong>Sukurta:</strong> {formatDate(job.created_at)}
            </div>
            <div className="date-item">
              <strong>Atnaujinta:</strong> {formatDate(job.updated_at)}
            </div>
            {job.published_at && (
              <div className="date-item">
                <strong>Publikuota:</strong> {formatDate(job.published_at)}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Uždaryti
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobViewModal;
